import { format, parse } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { HHMMRegex, HHMMSSRegex } from './consts/regex';
/**
 * It allows to handle dates with timezone
 * in a restricted way but avoiding the most common errors.
 * Internally it works by storing a string with the date in UTC and a timezone value.
 * ### Example
 * ```js
 *    import { TimezonedDateTime } from '@tsyl/radon'
 *    const timezonedObj = new TimezonedDateTime("America/Los_Angeles")
 *    timezonedObj.setZonedDateTime('2020-04-20 11:12:12')
 *    console.log(timezonedObj.toString())
 *    // 2020-04-20T18:12:12.000Z (UTC value)
 *    console.log(timezonedObj.format("MM-yyyy-dd HH:mm:ss"))
 *    // 2020-04-20 11:12:12 (Local value)
 *    timezonedObj.setZonedTime('23:00:12')
 *    console.log(timezonedObj.toString())
 *    // 2020-04-20TT06:00:12.000Z (UTC value)
 *    console.log(timezonedObj.format("MM-yyyy-dd HH:mm:ss"))
 *    // 2020-04-20 23:00:12 (Local value)
 * ```
 */
export default class TimezonedDateTime {
    /**
     * Create object with a timezone and optional a default value in utc.
     * @param timezone IANA time zone name
     * @param utcDate Default date in utc (ISO format, It must end in Z)
     */
    constructor(timezone, utcDate) {
        this.timezone = timezone;
        this.setUTC(utcDate);
    }
    /**
     * Set an UTC datetime
     * @param utcDate date in utc (ISO format, It has to end in Z)
     * @throws When param is not in UTC
     */
    setUTC(utcDate) {
        if (utcDate) {
            if (checkStringUtc(utcDate)) {
                this.utcDate = new Date(utcDate).toISOString();
            }
            else {
                throw new Error('Wrong type data, use a string with UTC timezone');
            }
        }
        else {
            this.utcDate = undefined;
        }
    }
    /**
     * Set a zoned time to the current date
     * @param time HH:mm or HH:mm:ss format
     * @throws When param is in wrong format
     */
    setZonedTime(time) {
        if (this.utcDate) {
            if (HHMMRegex.test(time) || HHMMSSRegex.test(time)) {
                const timeSplited = time.split(':').map((n) => parseInt(n));
                const zonedTime = utcToZonedTime(this.utcDate, this.timezone);
                zonedTime.setHours(timeSplited[0]);
                zonedTime.setMinutes(timeSplited[1]);
                if (timeSplited[2] !== undefined) {
                    zonedTime.setSeconds(timeSplited[2]);
                }
                this.utcDate = zonedTimeToUtc(zonedTime, this.timezone).toISOString();
            }
            else {
                throw new Error('Wrong time format. Use HH:mm or HH:mm:ss');
            }
        }
    }
    /**
     * Change the date without changing the time of the current date
     * @param date date string. If it is in a format that JavaScript understands,
     * it is not necessary to pass it the formatStr parameter
     * @param formatStr format of date to parse
     */
    setZonedDate(date, formatStr) {
        let formattedDate;
        if (formatStr) {
            formattedDate = format(parse(date, formatStr, new Date()), 'yyyy-MM-dd');
        }
        else {
            formattedDate = date;
        }
        const actualZonedTime = this.format('HH:mm:ss');
        const dateTimeZonedString = formattedDate + 'T' + actualZonedTime;
        this.utcDate = zonedTimeStringToUtcString(dateTimeZonedString, this.timezone);
    }
    /**
     * Set zoned datetime
     * @param date datetime string. If it is in a format that JavaScript understands,
     * it is not necessary to pass it the formatStr parameter
     * @param formatStr format of datetime to parse
     */
    setZonedDateTime(date, formatStr) {
        let formattedDate;
        if (formatStr) {
            formattedDate = format(parse(date, formatStr, new Date()), "yyyy-MM-dd'T'HH:mm:ss");
        }
        else {
            formattedDate = date;
        }
        this.utcDate = zonedTimeStringToUtcString(formattedDate, this.timezone);
    }
    /**
     * Zoned date formatted.
     * @param format format of returned value
     * @returns The zoned time formated.
     */
    format(format = "yyyy-MM-dd' 'HH:mm:ss") {
        if (this.utcDate) {
            return UtcStringToZonedTimeStringFormatted(this.utcDate, this.timezone, format);
        }
        else {
            return '';
        }
    }
    /**
     * Returns UTC date string
     */
    toString() {
        return this.utcDate;
    }
}
function checkStringUtc(string) {
    return typeof string === 'string' && string.endsWith('Z');
}
/**
 * Convert zoned datetime string to utc datetime string
 * @param date date string. Must be without time zone.
 * @param timezone Timezone IANA format
 * @throws When date has timezone
 */
export function zonedTimeStringToUtcString(date, timezone) {
    if (date) {
        if (typeof date !== 'string' || checkStringUtc(date)) {
            throw new Error('Wrong type data, use a string without timezone');
        }
        else {
            return zonedTimeToUtc(date, timezone).toISOString();
        }
    }
    return date;
}
/**
 * Convert utc string to zoned time string formatted
 * @param date date string. Must be with time zone.
 * @param timezone Timezone IANA format
 * @param formatstr date format
 * @throws When date has not timezone
 */
export function UtcStringToZonedTimeStringFormatted(utcDate, timezone, formatstr) {
    if (utcDate) {
        if (!checkStringUtc(utcDate)) {
            throw new Error('Wrong type data, use a string with UTC timezone');
        }
        const timezoned = utcToZonedTime(utcDate, timezone);
        return format(timezoned, formatstr);
    }
    return utcDate;
}
/**
 * Adds the UTC mark to an unmarked UTC date.
 * WARN: It does not convert dates, the date must already be UTC
 * @param date date string.
 * @returns date with UTC mark.
 */
export function utcVal(date) {
    if (date) {
        if (date.endsWith('Z')) {
            return date;
        }
        else {
            /**
             * Maybe need check date type.
             */
            return date.substr(0, 19) + 'Z';
        }
    }
    else {
        return date;
    }
}
//# sourceMappingURL=data:application/json;base64,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